import type {
  InboundAction,
  InboundAssetUrlCandidate,
  InboundCustomerThread,
  InboundLocationMapping,
  IdeaRoomShedSuiteCorrelation,
} from "@/lib/orderIntakeCustomersClient";

const ASSET_LABELS: Record<string, string> = {
  order: "Order document",
  floor_plan: "Floor plan",
  iso: "ISO view",
  front: "Front view",
  left: "Left view",
  back: "Back view",
  right: "Right view",
  social: "Social preview",
  render: "Rendering",
  configurator: "Configurator",
};

export function inboundAssetLabel(asset: Pick<InboundAssetUrlCandidate, "asset_kind">): string {
  return ASSET_LABELS[asset.asset_kind] ?? asset.asset_kind.replaceAll("_", " ");
}

export function inboundAssetStatusLabel(
  asset: Pick<InboundAssetUrlCandidate, "fetch_status">
): string {
  return {
    available: "Available",
    pending: "Saving…",
    imported: "Saved",
    error: "Save failed",
  }[asset.fetch_status];
}

export function correlationMatchLabel(
  correlation: Pick<IdeaRoomShedSuiteCorrelation, "match_status">
): string {
  return {
    awaiting_quote: "Standalone design",
    quote_matched: "Quote matched",
    order_matched: "Order matched",
    ambiguous: "Match needs review",
  }[correlation.match_status];
}

export function correlationImportLabel(
  correlation: Pick<
    IdeaRoomShedSuiteCorrelation,
    "match_status" | "import_status" | "import_error"
  >
): string {
  if (correlation.import_status === "imported") return "Floor plan imported";
  if (correlation.import_status === "not_available") return "Floor plan not available";
  if (correlation.import_status === "soft_failed") {
    return correlation.import_error
      ? `Floor plan import error: ${correlation.import_error}`
      : "Floor plan import error; retry available";
  }
  return correlation.match_status === "order_matched"
    ? "Floor plan import pending"
    : "Floor plan waits for exact order match";
}

export function visibleInboundActions(
  actions: InboundAction[],
  showLowValue: boolean
): InboundAction[] {
  return showLowValue ? actions : actions.filter((action) => !action.low_value);
}

export function hiddenLowValueActionCount(actions: InboundAction[]): number {
  return actions.filter((action) => action.low_value).length;
}

export function ideaRoomActivityLabel(
  action: Pick<InboundAction, "event_type" | "payload">
): string {
  const order =
    action.payload.order &&
    typeof action.payload.order === "object" &&
    !Array.isArray(action.payload.order)
      ? (action.payload.order as Record<string, unknown>)
      : null;
  const rawStatus =
    typeof order?.status === "string" ? order.status.trim().toLowerCase() : "";
  const status = rawStatus === "quote" ? "Quote" : rawStatus === "save" ? "Save" : "Unknown";
  const event = action.event_type.trim()
    ? action.event_type.trim().replaceAll("_", " ").replace(/^\w/, (value) => value.toUpperCase())
    : "Unknown event";
  return `${event} · ${status}`;
}

export function confirmedThreadLocation(
  thread: InboundCustomerThread
): InboundLocationMapping | null {
  return thread.location_mappings.find((mapping) => mapping.mapping_status === "confirmed") ?? null;
}

export function threadLocationHints(thread: InboundCustomerThread): string[] {
  const snapshots = thread.location_mappings
    .map((mapping) => mapping.source_snapshot)
    .filter((snapshot): snapshot is Record<string, unknown> => Boolean(snapshot));
  const values = [
    ...thread.source_location_keys,
    ...snapshots.flatMap((snapshot) =>
      ["dealerKey", "dealerName", "email", "city", "address"]
        .map((key) => snapshot[key])
        .filter((value): value is string => typeof value === "string" && value.trim() !== "")
    ),
  ];
  return [...new Set(values)];
}

export function customerDisplayName(profile: {
  display_name: string | null;
  email: string | null;
  phone: string | null;
}): string {
  return profile.display_name || profile.email || profile.phone || "Unknown customer";
}

export function formatInboundDate(value: string | null | undefined): string {
  if (!value) return "Unknown time";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}
